import React, { useEffect } from 'react';
import loadable from '@loadable/component';
import PropTypes from 'prop-types';
import Loading from 'Components/Loading';
import ApplicationLayout from 'Components/ApplicationLayout';
import pMinDelay from 'p-min-delay';
import { useDispatch, useSelector } from 'react-redux';
import { closeModal } from 'Store/actions';
import RenderActiveModals from './Modals/RenderActiveModals';
import { findInHippodrome } from 'Utils/horseRacesNameMapper';
import { TJK_URL_SUFFIX } from 'Store/constants/horseRacing';
import { getAllHippodromes } from 'Store/actions/horseRacing/getRaceInfos';
import EnvironmentManager from 'Utils/environmentManager';
import EmptyState from 'Components/EmptyState';
import History from 'Store/history';
import TjkLayout from './Layout';
import { betSlipClosed } from 'Store/actions';
import LocalStorage from 'Utils/localStorage';

const HomePage = loadable(() => pMinDelay(import('./Home'), 300), {
  fallback: <Loading />
});

const TjkProgram = loadable(() => pMinDelay(import('./Program'), 300), {
  fallback: <Loading />
});

const FixedOddProgram = loadable(() => pMinDelay(import('./Program'), 300), {
  fallback: <Loading />
});

const HorseRacingResults = loadable(() => pMinDelay(import('./Results'), 300), {
  fallback: <Loading />
});

const Probables = loadable(() => pMinDelay(import('./Probables'), 300), {
  fallback: <Loading />
});

const ResultsDetails = loadable(() => pMinDelay(import('./ResultsDetails'), 300), {
  fallback: <Loading />
});

const AGF = loadable(() => pMinDelay(import('./AGF'), 300), {
  fallback: <Loading />
});

const ResultsRunDetails = loadable(() => pMinDelay(import('./ResultsRunDetails'), 300), {
  fallback: <Loading />
});

const AuthorComments = loadable(() => pMinDelay(import('./AuthorComments'), 300), {
  fallback: <Loading />
});

const AuthorDetails = loadable(() => pMinDelay(import('./AuthorComments/AuthorDetails'), 300), {
  fallback: <Loading />
});

const UpcomingRaces = loadable(() => pMinDelay(import('./UpcomingRaces'), 300), {
  fallback: <Loading />
});

const Coupons = loadable(() => pMinDelay(import('./Coupons'), 300), {
  fallback: <Loading />
});

const CouponDetail = loadable(() => pMinDelay(import('./Coupons/CouponDetail'), 300), {
  fallback: <Loading />
});

const CouponShare = loadable(() => pMinDelay(import('./CouponShare'), 300), {
  fallback: <Loading />
});

const HorseCard = loadable(() => pMinDelay(import('./HorseCardWidget'), 300), {
  fallback: <Loading />
});

const RunDetailWidget = loadable(() => pMinDelay(import('./RunDetailWidget'), 300), {
  fallback: <Loading />
});

const TjkTvPage = loadable(() => pMinDelay(import('./TjkTvPage'), 300), {
  fallback: <Loading />
});

const NotFound = loadable(() => pMinDelay(import('../NotFound'), 300), {
  fallback: <Loading />
});

const AsyncHorseRacing = props => {
  const { hippodrome, raceNumber, author, couponId } = props.match.params;
  const { page } = props;
  const modalData = useSelector(state => state.modalData);
  const dispatch = useDispatch();
  const allHippodromes = useSelector(state => state.horseRacing.allHippodromes);
  const isBetslipOpen = useSelector(state => state.betSlip?.isOpen);
  const isServerSide = typeof window === 'undefined' || typeof document === 'undefined';

  const isTjkFetched = EnvironmentManager.isTjkMessagesReady();
  const hasTjkMessages = EnvironmentManager.tjkMessages
                          && EnvironmentManager.tjkMessages.length > 0;

  if (isTjkFetched && !hasTjkMessages && !isServerSide) {
    return <NotFound { ...props } />;
  }

  const isModalOpen = modalNameRequest => {
    const { modalNames } = modalData;
    return modalNames.some(modalName => modalName === modalNameRequest);
  };

  function closeModalFunc (modalName) {
    dispatch(closeModal(modalName));
  }

  useEffect(() => { // tjk routing yapısının çalışması için çoğu sayfada zorunlu olan tüm hipodromlar çekilir. gelmediği durumlarda zorunlu olan sayfalar için tjk kapatılır.
    if (!allHippodromes.data.length) {
      dispatch(getAllHippodromes());
    }
    const tjkTVLink = document.querySelector('[title="TJK TV"]');
    if (tjkTVLink) {
      tjkTVLink.removeAttribute('href');
      tjkTVLink.style.cursor = 'pointer';
      tjkTVLink.addEventListener('click', () => {
        sessionStorage.setItem('watch_tjktv', 'true');
        History.push('/tjk/at-yarisi-bulteni/');
      });
    }
    // subscribeTopic();
  }, []);

  useEffect(()=> {
    if (isBetslipOpen) {
      dispatch(betSlipClosed());
      LocalStorage.set('betSlipToggler', false);
    }
  }, [isBetslipOpen]);

  const tjkUrlValidation = () => {
    let data = {
      isValid: true,
      hippodrome: null
    };

    if (page === TJK_URL_SUFFIX.program.page || page === TJK_URL_SUFFIX.result.page || page === TJK_URL_SUFFIX.agf.page) {
      if (!hippodrome) {
        return data;
      }

      if (hippodrome) {
        const hip = findInHippodrome(hippodrome, allHippodromes.data);

        if (hip) {
          data.hippodrome = hip;
          return data;
        }
      }

      data.isValid = false;
      return data;
    }

    return data;
  };

  let pageContent = null;
  let isHipodromesRequired = true; // tüm hipodromlar bu sayfa için zorunlu mu ?, zorunlu olamayan sayfalar false e çekmeli
  const validateChecker = tjkUrlValidation();
  let showFooter = true;

  const defaultHippodrome = validateChecker.isValid ? validateChecker.hippodrome : null;
  let isNotFoundPage = false;
  if (validateChecker.isValid || isServerSide) {
    switch (page) {
      case TJK_URL_SUFFIX.home.page:
        pageContent = <HomePage { ...props } />;
        break;
      case TJK_URL_SUFFIX.program.page:
        pageContent = <TjkProgram { ...props } defaultHippodrome={ defaultHippodrome } search={ props.location.search } isHipodromeOnPath={ hippodrome } />;
        showFooter = false;
        break;
      case TJK_URL_SUFFIX.fixedOdd.page:
        pageContent = <FixedOddProgram { ...props } defaultHippodrome={ defaultHippodrome } search={ props.location.search } isHipodromeOnPath={ hippodrome } />;
        showFooter = false;
        break;
      case TJK_URL_SUFFIX.result.page:
        if (hippodrome && raceNumber) {
          pageContent = <ResultsRunDetails { ...props } defaultHippodrome={ defaultHippodrome } search={ props.location.search } raceNumber={ raceNumber } />;
        } else if (hippodrome) {
          pageContent = <ResultsDetails { ...props } defaultHippodrome={ defaultHippodrome } search={ props.location.search } />;
        } else {
          pageContent = <HorseRacingResults { ...props } defaultHippodrome={ defaultHippodrome } search={ props.location.search } />;
        }
        break;
      case TJK_URL_SUFFIX.probables.page:
        pageContent = <Probables { ...props } search={ props.location.search } />;
        break;
      case TJK_URL_SUFFIX.agf.page:
        pageContent = <AGF { ...props } defaultHippodrome={ defaultHippodrome } search={ props.location.search } />;
        break;
      case TJK_URL_SUFFIX.author.page:
        isHipodromesRequired = false;
        if (author) {
          pageContent = <AuthorDetails author={ author } { ...props } />;
        } else {
          pageContent = <AuthorComments { ...props } />;
        }
        break;
      case TJK_URL_SUFFIX.upcomingRaces.page:
        pageContent = <UpcomingRaces { ...props } search={ props.location.search } />;
        break;
      case TJK_URL_SUFFIX.coupons.page:
        isHipodromesRequired = false;
        if (couponId) {
          pageContent = <CouponDetail { ...props } couponId={ couponId } />;
        } else {
          pageContent = <Coupons { ...props } search={ props.location.search } />;
        }
        break;
      case TJK_URL_SUFFIX.shareCoupons.page:
      case TJK_URL_SUFFIX.shareFixedOddCoupons.page:
        isHipodromesRequired = false;
        pageContent = <CouponShare { ...props } couponId={ couponId } search={ props.location.search } />;
        break;
      case TJK_URL_SUFFIX.sharedCoupon.page:
      case TJK_URL_SUFFIX.sharedFixedOddCoupon.page:
        isHipodromesRequired = false;
        if (couponId) {
          pageContent = <CouponDetail { ...props } isExternal couponId={ couponId } />;
        }
        break;
      case TJK_URL_SUFFIX.horseCard.page:
        pageContent = <HorseCard { ...props } />;
        break;
      case TJK_URL_SUFFIX.runDetailWidget.page:
        pageContent = <RunDetailWidget { ...props } />;
        break;
      case TJK_URL_SUFFIX.tjkTv.page:
        pageContent = <TjkTvPage { ...props } />;
        break;
      case TJK_URL_SUFFIX.tjkTvExternal.page:
        pageContent = <TjkTvPage { ...props } />;
        break;
      default:
        isNotFoundPage = true;
        pageContent = <NotFound { ...props } />;
    }
  } else {
    isNotFoundPage = true;
    pageContent = <NotFound { ...props } />;
  }

  const checkHippodromesEmptyStates = () => {
    const hippodromesInfoEmpty = !allHippodromes.isLoading && (allHippodromes.error || allHippodromes.data.length === 0) && isHipodromesRequired;

    const errorText = EnvironmentManager.getMessageText({ key: 'description_tjk_mw_races_error' });
    const buttonText = EnvironmentManager.getMessageText({ key: 'button_turn_back_to_santra' });

    if (hippodromesInfoEmpty) {
      return (
        <div className='probables-empty-page'>
          <EmptyState action={ () => History.push('/') } buttonText={ buttonText }>
            { errorText }
          </EmptyState>
        </div>
      );
    }

    return null;
  };

  const headerType = () =>  {
    if ((page === TJK_URL_SUFFIX.home.page && props.userAgent.device?.type !== 'desktop') || isNotFoundPage) {
      return 'mobile-none';
    }
    return 'full';
  };

  const pageConfig = {
    headerType: headerType(),
    footerType: !showFooter && props.userAgent.device?.type !== 'desktop' ? 'none' : 'full',
    navigationType: 'horse-racing',
    helmet: {
      ...(page === 'at-karti' ? { meta: [{ name: 'robots', content: 'noindex' }] } : {}),
      bodyAttributes: {
        class: `${props.userAgent.device?.type} hide-betslip ${page === TJK_URL_SUFFIX.coupons.page && !couponId ? 'bg-green' : ''} ${page === TJK_URL_SUFFIX.shareCoupons.page ? 'hide-horse-racing-betslip hide-sticky-navigation' : ''}`
      },
      link: [
        {
          rel: 'stylesheet',
          type: 'text/css',
          href: '/public/thirdParty/video-js/video-js.min.css'
        }
      ],
    }
  };

  return (
    <ApplicationLayout { ...pageConfig } { ...props }>
      { checkHippodromesEmptyStates() ?? <TjkLayout page={ page } isNotFoundPage={ isNotFoundPage }> { pageContent } </TjkLayout> }
      <RenderActiveModals
        modalData={ modalData }
        isModalOpen={ isModalOpen }
        closeModal={ name => {
          closeModalFunc(name);
        } }
      />
    </ApplicationLayout>
  );
};

AsyncHorseRacing.propTypes = {
  userAgent: PropTypes.object,
  history: PropTypes.object,
  match: PropTypes.object,
  location: PropTypes.object,
  page: PropTypes.string,
};

export default AsyncHorseRacing;
